<template lang="pug">

.report-root
    general-title(
        title="人群属性",
        description="分析访客行为，包括性别、年龄、学历和职业。通过人群画像来定位网站的核心用户",
        pos="right",
        :date="timeSpan"
    )
    CompTimeSpan(
        :mintspan="7",
        :compared="compared",
        @rangeChange="resetCompared",
        @compRangeChange="compRangeChange"
    )
    .zone.top-zone
        .top-zone-cell(width="33.3333%")
            div.mr20
                TableTitle(icon="fa fa-user-o" title="性别比例")

                PieChart(
                    :fromAttr="fromAttr",
                    :legend="genderLegend",
                    :comp="compared",
                    :series="gender_series",
                    :option="pieConf",
                    :loading="genderLoading"
                )
            
        .top-zone-cell(width="33.3333%")
            div.mr20
                TableTitle(icon="fa fa-leaf", title="年龄分布")

                BarChart(
                    :comp="compared",
                    :initXAxis="ageXAxis",
                    :legend="commonLegend",
                    :series="age_series", dir="vertical", 
                    :color="ageColor",
                    :loading="ageLoading"
                )

        .top-zone-cell(width="33.3333%")
            div
                TableTitle(icon="fa fa-graduation-cap", title="学历分布")

                BarChart(
                    :comp="compared",
                    :initXAxis="eduXAxis",
                    :legend="commonLegend",
                    :series="edu_series", dir="vertical", 
                    :color="eduColor",
                    :loading="eduLoading"
                )

    .zone
        TableTitle(icon="fa fa-briefcase", title="职业分布")

        BarChart(
            :comp="compared",
            :initXAxis="jobInitXAxis",
            :legend="commonLegend",
            :color="jobColor",
            :series="job_series" dir="vertical", 
            :loading="jobLoading"
        )
</template>

<script>
/* global _ */
import { mapActions } from 'vuex'
import { getDateString } from '../../script/util'
import { USER_ATTR_MAP } from './constants'

export default {
  data () {
    const today = getDateString.today()

    return {
      timeSpan: { start: today, end: today },
      fromAttr: true,
      genderSeries: [],
      genderLoading: true,
      ageXAxis: _.reduce(USER_ATTR_MAP.age, (result, val, key) => {
        result.push(val)
        return result
      }, []),
      ageSeries: [],
      ageLoading: true,
      eduSeries: [],
      eduXAxis: _.reduce(USER_ATTR_MAP.edu, (result, val, key) => {
        result.push(val)
        return result
      }, []),
      eduLoading: true,
      jobInitXAxis: [],
      jobSeries: [],
      jobLoading: true,
      compareStart: '',
      compareEnd: '',
      compared: this.$store.state.web.compared
    }
  },
  computed: {
    gender_series () {
      return this.seriesFinal('gender')
    },
    age_series () {
      return this.seriesFinal('age')
    },
    edu_series () {
      return this.seriesFinal('edu')
    },
    job_series () {
      return this.seriesFinal('job')
    },
    genderLegend () {
      let legend = []
      if (!this.compared) {
        legend = ['男性', '女性']
      } else {
        legend = ['男性', '女性', '对比男性', '对比女性']
      }
      return legend
    },

    commonLegend () {
      let legend = []
      if (!this.compared) {
        legend = ['本期']
      } else {
        legend = ['本期', '对比时段']
      }
      return legend
    },
    ageColor () {
      if (!this.compared) {
        return '#f2c230'
      } else {
        return ['#f2c230', '#6390a6']
      }
    },
    eduColor () {
      if (!this.compared) {
        return '#bf86b6'
      } else {
        return ['#bf86b6', '#6390a6']
      }
    },
    jobColor () {
      if (!this.compared) {
        return '#56bf8b'
      } else {
        return ['#56bf8b', '#6390a6']
      }
    },
    domainId () {
      return this.$store.state.domainId
    },
    params () {
      let param = {
        domainId: this.domainId,
        start: this.$store.state.start,
        end: this.$store.state.end,
        cycle: this.$store.state.start === this.$store.state.end ? 'hour' : 'day',
        timespan: new Date().getTime(),
        isasc: 1,
        order: 'pv'
      }
      if (this.compared) {
        param = _.assignIn({
          compareStart: this.compareStart,
          compareEnd: this.compareEnd
        }, param)
      }
      return param
    },
    pieConf () {
      return {
        legend: { show: true },
        tooltipFormatter: '{b}:{d}%',
        labelFormatter: '{b}'
      }
    }
  },
  watch: {
    params: {
      handler () {
        this.getDataList()
      },
      deep: true
    }
  },
  mounted () {
    this.getDataList()
  },
  methods: {
    ...mapActions('web', {
      setCompared: 'SET_COMPARED'
    }),
    // 批量获取数据
    getDataList () {
      ['gender', 'age', 'edu', 'job'].forEach(type => {
        this.fetch(type)
      })
    },
    // is data compared or not
    isCompared (item) {
      /* eslint-disable no-undefined */
      return item.compared !== null && item.compared !== undefined &&
        item.compared === 'compare'
    },
    isNotCompared (item) {
      if (!item.compared) return true
    },

    // generate list
    seriesFinal (type) {
      let tempSeries = []
      const series = []
      switch (type) {
        case 'gender': tempSeries = this.genderSeries; break
        case 'age': tempSeries = this.ageSeries; break
        case 'edu': tempSeries = this.eduSeries; break
        case 'job':
          if (!this.compared) {
            tempSeries = _.reverse(this.jobSeries)
          } else {
            tempSeries = this.jobSeries
          }
          break
        default: break
      }

      if (this.compared) {
        const comp1 = []
        let curSeries = tempSeries.filter(this.isNotCompared)
        let compSeries = tempSeries.filter(this.isCompared)
        // reverse current series and get xAxis for jobSeries
        if (type === 'job') {
          curSeries = curSeries.sort((a, b) => b.value - a.value)
          const namesArray = Array.from(new Set(curSeries.map(i => i.name).concat(compSeries.map(i => i.name))))
          this.jobInitXAxis = namesArray
          if (compSeries.length > 0) {
            // insert values one by one in sort of the names of xaxis below
            const l = namesArray.length
            for (let i = 0; i < l; i++) {
              for (let j = 0; j < l; j++) {
                /* eslint-disable max-depth */
                if (compSeries[j] === void (0)) {
                  continue
                }
                if (compSeries[j].name === namesArray[i]) {
                  comp1.push(compSeries[j])
                  continue
                }
              }
            }
            compSeries = comp1
          }
        }

        const defaultObj = { }
        if (type !== 'gender') {
          series.push(_.assignIn({
            type: 'bar',
            name: '本期',
            data: curSeries
          }, defaultObj))
          series.push(_.assignIn({
            type: 'bar',
            name: '对比时段',
            data: compSeries
          }, defaultObj))
        } else {
          series.push(_.assignIn({
            type: 'pie',
            name: ['男性', '女性'],
            data: curSeries
          }, defaultObj))
          series.push(_.assignIn({
            type: 'pie',
            name: ['对比男性', '对比女性'],
            data: compSeries
          }, defaultObj))
        }
        return series
      } else {
        return tempSeries
      }
    },

    fetch (type) {
      if (!this.domainId) return Promise.resolve(null)
      /* eslint-disable prefer-template */
      this[type + 'Loading'] = true
      return this.$http.get(`/api/report/${type}`, {
        params: _.extend({}, this.params, { groupby: type })
      }).then(res => {
        return res.data
      }).catch(e => {
        return {
          errno: 99999,
          errmsg: '请求失败'
        }
      }).then(({errno, errmsg, list = []}) => {
        if (errno) this.$Modal.warn(errmsg)
        this[type + 'Loading'] = false

        if (type === 'age' || type === 'edu') {
          list = list.sort((a, b) => a[type] - b[type])
        }

        this[type + 'Series'] = list.map(item => {
          const code = item[type]
          let name = USER_ATTR_MAP[type][code]
          const value = item.pv
          const compared = item.dataCompare
          // 返回name处理
          if (type === 'gender') {
            if (compared === 'compare') {
              name = USER_ATTR_MAP[type][code] === '女' ? '对比女性' : '对比男性'
            } else {
              name = USER_ATTR_MAP[type][code] === '女' ? '女性' : '男性'
            }
          }
          // 对比数据处理
          return { name, value, compared }
        })
      })
    },

    // 对比更新
    compRangeChange ({compared, compStart, compEnd}) {
      this.compared = compared
      this.compareStart = compStart
      this.compareEnd = compEnd
      this.setCompared({compared: compared, compStart: this.compareStart, compEnd: this.compareEnd})
    },
    // 重置compared并获取数据
    resetCompared (start, end) {
      this.compared = false
      this.setCompared({compared: false, compStart: '', compEnd: ''})
    }
  }
}
</script>
<style scoped>

@import '../../css/base/variables.css';

.top-zone,
.zone {
    overflow: hidden;
    display: table;
    table-layout: fixed;
    width: 100%; 
}
.zone{
    margin-bottom: 20px;
    background: #fff;
    border-bottom: 1px solid #e5e5e5;
}
.zone heading{ padding-right: 1rem;}
.top-zone{
    border: 0;
    background: transparent;
}
.zone-header{ border-bottom: 1px solid #e5e5e5;}
.top-zone-cell{
    background: transparent;
    display: table-cell;
    & > div{
    background: #fff;
        border: 1px solid #e5e5e5;
    }
}
.title {
    padding: 12px 20px;
    font-size: 16px;
    margin-top: 0;
    font-weight: bold;
}
.table-div {
    display: table;
    table-layout: fixed;
    width: 100%;
    margin-bottom: 20px;

    & > div {
        display: table-cell;
        width: 50%;
    }
}
.charts { height: 301px;}
.circle {
    display: inline-block;
    width: 72px;
    height: 72px;
    padding: 18px;
    margin-right: 10px;
    line-height: 1.6em;
    color: white;
    border-radius: 50%;
    text-align: center;
    background: red;
    vertical-align: middle;
}
.male {
    background: #638ca6;
}
.female {
    background: var(--primary-color);;
}
.icon-proportion {
    display: inline-block;

    & > span {
        position: relative;
        display: inline-block;
        width: 36px;
        height: 36px;
        margin: 4px;
        text-align: center;
        border-radius: 50%;
        line-height: 36px;
        background: #c3c7d0;
        color: #fff;
        font-size: 20px;
        vertical-align: middle;
        overflow: hidden;

        & > .fa {
            position: relative;
            z-index: 2;
        }
        & > .male-ico-bg-part,
        & > .female-ico-bg-part {
            position: absolute;
            z-index: 1;
            left: 0;
            top: 0;
            width: 0%;
            height: 100%;
            background: inherit;
        }

        & > .male-ico-bg-part,
        &.male-active {
            background: #00abef;
        }

        & > .female-ico-bg-part,
        &.female-active {
            background: #fc6981;
        }
    }
}
.row-gender {
    margin: 20px auto;
    line-height: 72px;
    text-align: center;
}
.mr20{ margin-right: 20px;}
</style>



// WEBPACK FOOTER //
// attributes.vue?2fe4f0a4